import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Field, NSelect, useFetch } from "@/components/common";
import { PhotoPicker, useUploadsPending } from "@/components/Photos";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, todayIso } from "@/lib/api";

export const CATS = { "": "Sans catégorie", vue_generale: "Vue générale", avant: "Dommage avant", arriere: "Dommage arrière", gauche: "Côté gauche", droit: "Côté droit", autre: "Autre" };
const QUESTIONS = [
  ["third_party", "Tiers impliqué"], ["drivable", "Véhicule encore roulant"], ["repair_needed", "Intervention ou réparation nécessaire"],
  ["amicable", "Constat amiable réalisé"], ["police", "Forces de l'ordre intervenues"],
];

function Choice({ value, onChange, options, testid }) {
  return (
    <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0,1fr))` }} data-testid={testid}>
      {options.map(([v, l]) => (
        <button key={String(v)} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
          className={`h-11 rounded-lg border-2 text-sm font-bold transition-colors ${value === v ? "bg-[#0E2D43] border-[#0E2D43] text-white" : "bg-white border-slate-200 text-slate-600"}`}>{l}</button>
      ))}
    </div>
  );
}

const nowTime = () => new Date().toTimeString().slice(0, 5);

export default function AccidentForm() {
  const { user } = useAuth();
  const nav = useNavigate();
  const vehicles = useFetch("/vehicles", { type: "livraison" });
  const drivers = useFetch("/drivers", {});
  const [f, setF] = useState({ date: todayIso(), time: nowTime(), vehicle_id: "", driver_id: "", location: "", description: "", comment: "",
    third_party: null, responsible: null, drivable: null, repair_needed: null, amicable: null, police: null });
  const [photos, setPhotos] = useState([]);
  const [hint, setHint] = useState(null);
  const [busy, setBusy] = useState(false);
  const uploading = useUploadsPending();
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (!f.vehicle_id) {
      setHint(null);
      return;
    }
    api.get("/accidents/suggest-driver", { params: { vehicle_id: f.vehicle_id, date: f.date } }).then(({ data }) => {
      setHint(data);
      if (data.driver_id) setF((x) => ({ ...x, driver_id: data.driver_id }));
    });
  }, [f.vehicle_id, f.date]);

  const submit = async (e) => {
    e.preventDefault();
    const missing = ["vehicle_id", "driver_id", "location", "description"].filter((k) => !f[k]?.trim());
    const unanswered = [...QUESTIONS.map((q) => q[0]), "responsible"].filter((k) => f[k] === null);
    if (missing.length || unanswered.length) return toast.error("Complétez tous les champs obligatoires et répondez à toutes les questions");
    setBusy(true);
    try {
      const { data } = await api.post("/accidents", { ...f, photos });
      toast.success(`Dossier ${data.number} enregistré${data.vehicle_immobilized ? " — véhicule passé en Immobilisé" : ""}`);
      nav(`/livraison/accidents/${data.id}`);
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  const box = "bg-white border border-slate-200 rounded-xl p-4 sm:p-5";
  return (
    <div className="max-w-2xl" data-testid="accident-form-page">
      <Link to="/livraison/accidents" className="inline-flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft className="w-4 h-4" /> Accidents / Sinistres</Link>
      <PageHeader overline="Véhicules de livraison" title="Déclarer un accident" />
      <form onSubmit={submit} className="space-y-4" data-testid="accident-form">
        <div className={`${box} grid sm:grid-cols-2 gap-4`}>
          <Field label="Date de l'accident *"><Input type="date" max={todayIso()} value={f.date} onChange={set("date")} className="h-11 bg-white" data-testid="acc-date-input" /></Field>
          <Field label="Heure approximative *"><Input type="time" value={f.time} onChange={set("time")} className="h-11 bg-white" data-testid="acc-time-input" /></Field>
          <Field label="Véhicule / immatriculation *"><NSelect value={f.vehicle_id} onChange={set("vehicle_id")} placeholder="Choisir…" testid="acc-vehicle-select"
            options={(vehicles.data || []).map((v) => ({ value: v.id, label: `${v.plate} · ${v.brand} ${v.model}` }))} /></Field>
          <Field label="Chauffeur concerné *">
            <NSelect value={f.driver_id} onChange={set("driver_id")} placeholder="Choisir…" testid="acc-driver-select" options={(drivers.data || []).map((d) => ({ value: d.id, label: d.name }))} />
            {hint?.driver_id && <span className="text-xs text-emerald-700" data-testid="acc-driver-hint">Proposé : chauffeur attribué au contrôle du {hint.check_date.split("-").reverse().join("/")} (modifiable)</span>}
          </Field>
          <Field label="Déclaration enregistrée par"><div className="h-11 px-3 flex items-center rounded-md bg-slate-50 border text-sm font-semibold" data-testid="acc-declared-by">{user.name}</div></Field>
          <Field label="Lieu de l'accident *"><Input value={f.location} onChange={set("location")} className="h-11 bg-white" placeholder="Adresse, parking client…" data-testid="acc-location-input" /></Field>
          <div className="sm:col-span-2"><Field label="Description courte des faits *"><Textarea rows={3} value={f.description} onChange={set("description")} className="bg-white" data-testid="acc-description-input" /></Field></div>
        </div>
        <div className={`${box} space-y-4`}>
          {QUESTIONS.slice(0, 1).map(([k, l]) => <Field key={k} label={`${l} *`}><Choice value={f[k]} onChange={set(k)} options={[[true, "Oui"], [false, "Non"]]} testid={`acc-${k}`} /></Field>)}
          <Field label="Accident responsable *"><Choice value={f.responsible} onChange={set("responsible")} options={[["oui", "Oui"], ["non", "Non"], ["a_determiner", "À déterminer"]]} testid="acc-responsible" /></Field>
          {QUESTIONS.slice(1).map(([k, l]) => <Field key={k} label={`${l} *`}><Choice value={f[k]} onChange={set(k)} options={[[true, "Oui"], [false, "Non"]]} testid={`acc-${k}`} /></Field>)}
          {f.drivable === false && <div className="text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-3" data-testid="acc-immobilize-warning">Le véhicule sera automatiquement passé au statut « Immobilisé ».</div>}
        </div>
        <div className={box}>
          <div className="font-semibold text-[#0E2D43] mb-3">Photos des dégâts</div>
          <PhotoPicker items={photos} onChange={setPhotos} categories={CATS} testid="acc-photos" />
        </div>
        <div className={box}><Field label="Commentaire complémentaire"><Textarea rows={2} value={f.comment} onChange={set("comment")} className="bg-white" data-testid="acc-comment-input" /></Field></div>
        <button type="submit" disabled={busy || uploading > 0} className="w-full h-14 rounded-lg bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold flex items-center justify-center gap-2 sticky bottom-3 shadow-lg disabled:bg-slate-300 disabled:text-slate-600" data-testid="acc-submit">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : uploading ? `Envoi des photos en cours (${uploading})…` : "Enregistrer la déclaration"}
        </button>
      </form>
    </div>
  );
}
